import { Component, OnInit, inject, signal, computed, Inject, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environment';
import {
  MatDialog,
  MatDialogRef,
  MatDialogModule,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import {
  DynamicTranslationService,
  TranslationApiService,
  Translation,
  AuthService,
} from '@gs-2.0/core';
import { ToastService } from '../../services/toast.service';
import { GeneralService, GeneralLanguage } from '@gs-2.0/core';
import { LanguageService, LanguageTranslations, CreateUpdateLanguagePayload } from '@gs-2.0/core';
import { TenantService } from '@gs-2.0/core';
import { TranslateLangPipe } from '../../pipes/translate-lang.pipe';
import { SharedModule } from '../../shared.module';
import { DropdownItem } from '../dropdown/dropdown.component';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from '../confirmation-dialog/confirmation-dialog.component';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap, map } from 'rxjs/operators';
import { normalizeSearchText } from '../../utils/text-normalization.util';

export interface TranslationManagerDialogData {
  languageCode?: string;
  isAddMode?: boolean;
  appType?: 'GS' | 'PORTAL' | null;
}

export interface TranslationEntry {
  key: string;
  value: string;
  originalValue: string;
  category: string;
  isDirty: boolean;
  description: string;
  englishTranslation: string;
}

@Component({
  selector: 'app-translation-manager-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    TranslateLangPipe,
    SharedModule,
    ScrollingModule,
  ],
  templateUrl: './translation-manager-dialog.component.html',
  styleUrls: ['./translation-manager-dialog.component.scss'],
})
export class TranslationManagerDialogComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<TranslationManagerDialogComponent>);
  private dialog = inject(MatDialog);
  private translationService = inject(DynamicTranslationService);
  private translationApi = inject(TranslationApiService);
  private generalService = inject(GeneralService);
  private languageService = inject(LanguageService);
  private tenantService = inject(TenantService);
  private authService = inject(AuthService);
  private http = inject(HttpClient);
  private toastService = inject(ToastService);

  constructor(@Optional() @Inject(MAT_DIALOG_DATA) public data?: TranslationManagerDialogData) {
    // Initialize dialogData signal after data is available
    this.dialogData.set(this.data);
  }

  // Signals for reactive state
  languages = signal<GeneralLanguage[]>([]);
  selectedLanguage = signal<GeneralLanguage | null>(null);
  categories = signal<DropdownItem[]>([]);
  selectedCategory = signal<string>('all');
  searchTerm = signal<string>('');
  translations = signal<TranslationEntry[]>([]);
  isLoading = signal<boolean>(false);
  unpublishedKeys = signal<string[]>([]);
  hasEmptyTarget = signal<boolean>(false); // Tracks if target translations were empty (new language with no saved translations)

  // Store dialog data as signals for better reactivity
  dialogData = signal<TranslationManagerDialogData | undefined>(undefined);

  // Model for search input (ngModel binding)
  searchTermModel = '';

  // Store current translations for synchronous access
  private currentTranslations: { [key: string]: string } = {};

  // Computed properties
  languageOptions = computed<DropdownItem[]>(() =>
    this.languages().map((lang) => ({
      id: lang.code,
      label: lang.name,
      // secondaryText: lang.code
    }))
  );

  categoryOptions = computed<DropdownItem[]>(() => {
    const allOption: DropdownItem = {
      id: 'all',
      label: this.translationService.instant('translation.manager.all_categories'),
    };
    return [allOption, ...this.categories()];
  });

  filteredTranslations = computed<TranslationEntry[]>(() => {
    let filtered = this.translations();

    // Filter by category
    if (this.selectedCategory() !== 'all') {
      filtered = filtered.filter((t) => t.category === this.selectedCategory());
    }

    // Filter by search term
    const term = normalizeSearchText(this.searchTerm());
    if (term) {
      filtered = filtered.filter(
        (t) =>
          normalizeSearchText(t.key).includes(term) ||
          normalizeSearchText(t.value).includes(term) ||
          normalizeSearchText(t.englishTranslation).includes(term) ||
          normalizeSearchText(t.originalValue).includes(term)
      );
    }

    return filtered;
  });

  dirtyTranslations = computed<TranslationEntry[]>(() =>
    this.translations().filter((t) => t.isDirty)
  );

  hasDirtyTranslations = computed<boolean>(() => this.dirtyTranslations().length > 0);

  totalFields = computed<number>(() => this.translations().length);

  translatedFields = computed<number>(() => {
    // Treat as ADD mode if: actually in ADD mode OR target was empty (new language with no saved translations)
    if (this.isAddMode() || this.hasEmptyTarget()) {
      // In ADD mode or UPDATE with empty target, only count fields where user has changed from English default
      return this.translations().filter(
        (t) => t.value && t.value.trim() !== '' && t.value !== t.englishTranslation
      ).length;
    } else {
      // In UPDATE mode with existing translations, count all non-empty fields
      return this.translations().filter((t) => t.value && t.value.trim() !== '').length;
    }
  });

  hasUnpublishedChanges = computed<boolean>(() => this.unpublishedKeys().length > 0);

  // Computed property for add mode
  isAddMode = computed<boolean>(() => {
    const data = this.dialogData();
    return data?.isAddMode === true;
  });

  // Computed properties for publish/unpublish button visibility
  isLiveLanguage = computed<boolean>(() => {
    const lang = this.selectedLanguage();
    return lang?.state === 'LIVE';
  });

  showPublishButton = computed<boolean>(() => {
    // Show publish button if language state is DRAFT
    return !this.isLiveLanguage();
  });

  showUnpublishButton = computed<boolean>(() => {
    // Show unpublish button if language state is LIVE
    return this.isLiveLanguage();
  });

  ngOnInit(): void {
    this.loadLanguages();
  }

  loadLanguages(): void {
    // Load languages from GeneralService instead of DynamicTranslationService
    this.generalService.getGeneralDropdowns().subscribe({
      next: (response) => {
        const languages = response.response.languages;
        this.languages.set(languages);

        let selectedLang: GeneralLanguage | undefined;

        // If languageCode was provided via dialog data, use that
        if (this.data?.languageCode) {
          selectedLang = languages.find(
            (lang) => lang.code.toLowerCase() === this.data!.languageCode?.toLowerCase()
          );
        }

        // If not found or not provided, default to English (using ISO 639-3 code 'eng')
        if (!selectedLang) {
          selectedLang = languages.find((lang) => lang.code.toLowerCase() === 'eng');
        }

        // Fallback to primary language if English is not available
        if (!selectedLang) {
          selectedLang = languages.find((lang) => lang.primaryLanguage);
        }

        // Last fallback: select the first available language
        if (!selectedLang && languages.length > 0) {
          selectedLang = languages[0];
        }

        if (selectedLang) {
          this.selectedLanguage.set(selectedLang);
          // Load translations after language is set
          this.loadTranslations();
        }
      },
      error: (error) => {
        console.error('Error loading languages:', error);
      },
    });
  }

  /**
   * Determines if a translations response is empty
   * Empty response format: { "response": {} }
   */
  private isEmptyTranslationsResponse(translations: LanguageTranslations): boolean {
    return !translations || Object.keys(translations).length === 0;
  }

  loadTranslations(): void {
    const selectedLang = this.selectedLanguage();
    if (!selectedLang) {
      return;
    }

    this.isLoading.set(true);

    // Check if we're in add mode
    const inAddMode = this.isAddMode();

    // Get appType for role-based filtering in translation manager.
    // Prefer appType passed via dialog data (e.g. fund-settings forces 'PORTAL'),
    // otherwise fall back to the user's role from AuthService.
    const appType =
      this.data?.appType !== undefined ? this.data.appType : this.authService.getAppType();

    // Use eng.json as the master source of truth for all translation keys
    // For PORTAL appType, use portal/eng.json which contains Portal-specific keys
    // These files are served from apps/gs-2.0/src/tenants/tb/i18n/ via project.json asset mapping
    const masterKeysFilePath =
      appType === 'PORTAL' ? 'assets/i18n/portal/eng.json' : 'assets/i18n/eng.json';

    // Load master keys and target translations first
    const initialLoad = forkJoin({
      masterKeys: this.http.get<LanguageTranslations>(masterKeysFilePath).pipe(
        catchError((error) => {
          console.error(`Master keys file not found at ${masterKeysFilePath}:`, error);
          return of({} as LanguageTranslations);
        })
      ),
      targetApiTranslations: inAddMode
        ? of({} as LanguageTranslations)
        : this.languageService.getLanguageTranslationsForLocale(selectedLang.code, appType).pipe(
            catchError((error) => {
              console.error(`Failed to load target translations for ${selectedLang.code}:`, error);
              return of({} as LanguageTranslations);
            })
          ),
    });

    // Conditionally load English API translations based on mode and target content.
    // Skip when target language is already English — targetApiTranslations is the same data.
    const loadRequest = initialLoad.pipe(
      switchMap(({ masterKeys, targetApiTranslations }) => {
        const isTargetEnglish = selectedLang.code === 'eng';
        const needsEnglishApi =
          !isTargetEnglish &&
          (inAddMode || this.isEmptyTranslationsResponse(targetApiTranslations) || !!appType);

        if (needsEnglishApi) {
          return this.languageService.getLanguageTranslationsForLocale('eng', appType).pipe(
            map((englishApiTranslations) => ({
              masterKeys,
              targetApiTranslations,
              englishApiTranslations,
            })),
            catchError((error) => {
              console.error('Failed to load English API translations:', error);
              return of({
                masterKeys,
                targetApiTranslations,
                englishApiTranslations: {} as LanguageTranslations,
              });
            })
          );
        } else {
          // When target is English, reuse targetApiTranslations as the English reference to avoid a duplicate call.
          const englishApiTranslations = isTargetEnglish
            ? targetApiTranslations
            : ({} as LanguageTranslations);
          return of({ masterKeys, targetApiTranslations, englishApiTranslations });
        }
      })
    );

    loadRequest.subscribe({
      next: ({ masterKeys, englishApiTranslations, targetApiTranslations }) => {
        // Store translations for synchronous access (merge all sources)
        this.currentTranslations = {
          ...masterKeys,
          ...englishApiTranslations,
          ...targetApiTranslations,
        };

        // Track if target was empty (new language with no saved translations)
        this.hasEmptyTarget.set(this.isEmptyTranslationsResponse(targetApiTranslations));

        const entries: TranslationEntry[] = [];
        const categorySet = new Set<string>();
        const unpublished: string[] = [];

        // Merge all keys from all sources
        // Always include masterKeys so that new keys from the JSON file
        // (not yet in the DB) appear as unpublished and can be saved.
        const allKeys = new Set<string>([
          ...Object.keys(masterKeys),
          ...Object.keys(englishApiTranslations),
          ...Object.keys(targetApiTranslations),
        ]);

        // Process all keys from both sources
        allKeys.forEach((key) => {
          const category = key.split('.')[0];
          categorySet.add(category);

          // Generate description from key
          const description = this.generateDescriptionFromKey(key);

          // Check if this key exists in the target API and master keys
          const existsInTargetApi = targetApiTranslations.hasOwnProperty(key);
          const existsInMaster = masterKeys.hasOwnProperty(key);

          // In add mode, show English as default but mark as dirty (needs translation)
          // In edit mode, use existing logic
          // Treat as ADD mode if: actually in ADD mode OR target response is empty (edge case: new language with no saved translations)
          const isEnglish = selectedLang.code === 'eng';
          const treatAsAddMode =
            inAddMode || this.isEmptyTranslationsResponse(targetApiTranslations);
          let value: string;
          let originalValue: string;
          let isDirty: boolean;

          if (treatAsAddMode) {
            // Add mode or UPDATE with empty target: Show English values as defaults (from masterKeys or englishApiTranslations for dynamic data)
            value = masterKeys[key] || englishApiTranslations[key] || '';
            originalValue = ''; // No original value in add mode
            isDirty = true; // Everything is dirty in add mode
            unpublished.push(key);
          } else {
            // Edit mode with existing translations
            value = existsInTargetApi
              ? targetApiTranslations[key]
              : isEnglish && existsInMaster
                ? masterKeys[key]
                : '';
            originalValue = existsInTargetApi
              ? targetApiTranslations[key]
              : isEnglish && existsInMaster
                ? masterKeys[key]
                : '';

            // Dirty if key is in the master JSON but not yet saved to the DB for this locale
            isDirty = !existsInTargetApi && existsInMaster;

            if (!existsInTargetApi && existsInMaster) {
              unpublished.push(key);
            }
          }

          // English translation from master keys or English API (for reference)
          const englishTranslation = masterKeys[key] || englishApiTranslations[key] || '';

          entries.push({
            key,
            value: value,
            originalValue: originalValue,
            category,
            isDirty: isDirty,
            description,
            englishTranslation,
          });
        });

        this.unpublishedKeys.set(unpublished);

        // Update categories
        const categoryItems: DropdownItem[] = Array.from(categorySet)
          .sort()
          .map((cat) => ({
            id: cat,
            label: cat,
          }));
        this.categories.set(categoryItems);

        this.translations.set(entries);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading translations:', error);
        this.isLoading.set(false);
      },
    });
  }

  onLanguageChange(languageId: string): void {
    const lang = this.languages().find((l) => l.code === languageId);
    if (lang) {
      this.selectedLanguage.set(lang);
      // Note: We do NOT call setCurrentLanguage here because that would change
      // the application's language. We only want to load translations for editing.
      this.loadTranslations();
    }
  }

  onCategoryChange(categoryId: string): void {
    this.selectedCategory.set(categoryId);
  }

  onSearchChange(value: string): void {
    this.searchTerm.set(value);
  }

  onTranslationChange(entry: TranslationEntry, newValue: string): void {
    entry.value = newValue;

    // Check if this is an unpublished key (not in API)
    const isUnpublished = this.unpublishedKeys().includes(entry.key);

    // Unpublished keys are always dirty (they need to be saved to API)
    // Published keys are dirty only if value differs from originalValue
    if (isUnpublished) {
      entry.isDirty = true;
    } else {
      entry.isDirty = entry.value !== entry.originalValue;
    }

    // Trigger change detection by updating the signal
    this.translations.set([...this.translations()]);
  }

  saveAllChanges(): void {
    const dirty = this.dirtyTranslations();
    const selectedLang = this.selectedLanguage();

    if (dirty.length === 0 || !selectedLang) return;

    this.isLoading.set(true);

    // Determine appType for saving (same logic as loadTranslations)
    const appType =
      this.data?.appType !== undefined ? this.data.appType : this.authService.getAppType();

    // Prepare bulk upsert payload with only changed items
    const items = dirty.map((entry) => ({
      key: entry.key,
      value: entry.value,
      description: entry.description,
      ...(appType ? { appType } : {}),
    }));

    // Use bulk upsert for both add and edit modes
    this.translationApi.bulkUpsertLanguageTranslations(selectedLang.code, items).subscribe({
      next: () => {
        // If we were in add mode, switch to edit mode after first save
        if (this.isAddMode()) {
          const currentData = this.dialogData();
          this.dialogData.set({
            ...currentData,
            isAddMode: false,
          });
        }

        // Reload translations from database to ensure we have fresh data
        this.loadTranslations();
      },
      error: (error) => {
        console.error('Error saving translations:', error);
        this.isLoading.set(false);
      },
    });
  }

  confirmDiscard(): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '512px',
      maxWidth: '90vw',
      panelClass: 'confirmation-dialog-container',
      data: {
        message: 'Are you sure you want to discard the updates?',
        confirmButtonText: 'translation.manager.discard',
        cancelButtonText: 'common.cancel',
        actionType: 'custom',
      } as ConfirmationDialogData,
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.discardChanges();
      }
    });
  }

  confirmPublish(): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '512px',
      maxWidth: '90vw',
      panelClass: 'confirmation-dialog-container',
      data: {
        message: 'Are you sure you want to publish this translation?',
        confirmButtonText: 'translation.manager.publish',
        cancelButtonText: 'common.cancel',
        actionType: 'custom',
      } as ConfirmationDialogData,
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.publish();
      }
    });
  }

  confirmUnpublish(): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '512px',
      maxWidth: '90vw',
      panelClass: 'confirmation-dialog-container',
      data: {
        message: 'Are you sure you want to unpublish this translation?',
        confirmButtonText: 'translation.manager.unpublish',
        cancelButtonText: 'common.cancel',
        actionType: 'custom',
      } as ConfirmationDialogData,
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.unpublish();
      }
    });
  }

  publish(): void {
    const selectedLang = this.selectedLanguage();

    if (!selectedLang || !selectedLang.uuid) {
      return;
    }

    this.isLoading.set(true);

    const payload: CreateUpdateLanguagePayload = {
      countryName: selectedLang.name,
      languageCode: selectedLang.code,
      state: 'LIVE',
      uuid: selectedLang.uuid,
    };

    this.languageService.createUpdateLanguage(payload).subscribe({
      next: (response) => {
        this.isLoading.set(false);
        // Update the selected language state locally
        selectedLang.state = 'LIVE';
        this.selectedLanguage.set({ ...selectedLang });
      },
      error: (error) => {
        this.isLoading.set(false);
        console.error('Failed to publish language:', error);
      },
    });
  }

  unpublish(): void {
    const selectedLang = this.selectedLanguage();

    if (!selectedLang || !selectedLang.uuid) {
      return;
    }

    this.isLoading.set(true);

    const payload: CreateUpdateLanguagePayload = {
      countryName: selectedLang.name,
      languageCode: selectedLang.code,
      state: 'DRAFT',
      uuid: selectedLang.uuid,
    };

    this.languageService.createUpdateLanguage(payload).subscribe({
      next: (response) => {
        this.isLoading.set(false);
        // Update the selected language state locally
        selectedLang.state = 'DRAFT';
        this.selectedLanguage.set({ ...selectedLang });
      },
      error: (error) => {
        this.isLoading.set(false);
        console.error('Failed to unpublish language:', error);
      },
    });
  }

  discardChanges(): void {
    // Revert all dirty translations to their original values
    this.translations().forEach((t) => {
      if (t.isDirty) {
        t.value = t.originalValue;

        // Unpublished keys should remain dirty even after discarding
        // because they still need to be saved to the API
        const isUnpublished = this.unpublishedKeys().includes(t.key);
        t.isDirty = isUnpublished;
      }
    });
    this.translations.set([...this.translations()]);
  }

  exportTranslations(): void {
    const langCode = this.selectedLanguage()?.code;
    if (!langCode) return;

    const appType =
      this.data?.appType !== undefined ? this.data.appType : this.authService.getAppType();

    const params: Record<string, string> = {};
    if (appType) {
      params['appType'] = appType;
    }

    this.http
      .get(`${environment.apiUrl}/v1/language/${langCode}/export/csv`, {
        responseType: 'blob',
        params,
      })
      .subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `translations-${langCode}-${Date.now()}.csv`;
          link.click();
          window.URL.revokeObjectURL(url);
        },
        error: (error) => {
          console.error('Error exporting translations:', error);
        },
      });
  }

  importTranslations(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    const langCode = this.selectedLanguage()?.code;
    if (!langCode) {
      const message = this.getTranslation('translation.manager.select_language_first');
      alert(message);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    this.isLoading.set(true);
    this.http.post(`${environment.apiUrl}/v1/language/${langCode}/import/csv`, formData).subscribe({
      next: () => {
        (event.target as HTMLInputElement).value = '';
        this.toastService.success('Language Successfully updated');
        this.loadTranslations();
      },
      error: (error) => {
        console.error('Error importing translations:', error);
        this.isLoading.set(false);
        (event.target as HTMLInputElement).value = '';
        const errorMessage: string = error?.error?.message || '';
        if (errorMessage.includes('Invalid CSV file')) {
          this.toastService.error(
            this.translationService.instant('translation.manager.invalid_csv_file')
          );
        } else {
          this.toastService.error('Error is encountered while processing the file');
        }
      },
    });
  }

  close(): void {
    if (this.hasDirtyTranslations()) {
      const confirmDialogRef = this.dialog.open(ConfirmationDialogComponent, {
        width: '512px',
        maxWidth: '90vw',
        panelClass: 'confirmation-dialog-container',
        data: {
          layout: 'detailed',
          title: 'translation.manager.unsaved_changes_title',
          description: 'translation.manager.unsaved_changes_description',
          cancelButtonText: 'translation.manager.continue_editing',
          confirmButtonText: 'translation.manager.discard_changes',
          actionType: 'custom',
        } as ConfirmationDialogData,
      });

      confirmDialogRef.afterClosed().subscribe((result: boolean) => {
        if (result) {
          this.dialogRef.close();
        }
        // If false/undefined, user cancelled - stay open
      });

      return;
    }
    this.dialogRef.close();
  }

  /**
   * Helper method to get translation synchronously
   */
  private getTranslation(key: string): string {
    return this.currentTranslations[key] || key;
  }

  trackByKey(_index: number, entry: TranslationEntry): string {
    return entry.key;
  }

  /**
   * Generate a human-readable description from a translation key
   */
  private generateDescriptionFromKey(key: string): string {
    // Remove the category prefix and convert to readable text
    const parts = key.split('.');
    const lastPart = parts[parts.length - 1];

    // Convert camelCase to Title Case with spaces
    return lastPart
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  }
}
